import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import { 
  FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, 
  FaSearch, FaSave, FaTimes, FaImage, FaArrowUp, FaArrowDown,
  FaPalette, FaLink
} from 'react-icons/fa';

const Banners = () => {
  const { can } = usePermission();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    position: '',
    image: null,
    cta_text: '',
    cta_url: '',
    text_color: '#FFFFFF',
    background_color: '#1D44E9',
    display_order: 0,
    active: true
  });

  const canEdit = can('content.edit');
  const canCreate = can('content.create');
  const canDelete = can('content.delete');

  const positionOptions = [
    { value: 'home-after-stats', label: 'Home - After Stats' },
    { value: 'home-after-hero', label: 'Home - After Hero' },
    { value: 'about-top', label: 'About - Top' },
    { value: 'services-top', label: 'Services - Top' },
    { value: 'portfolio-top', label: 'Portfolio - Top' },
    { value: 'contact-top', label: 'Contact - Top' }
  ];

  useEffect(() => {
    fetchBanners();
  }, [searchTerm]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm
      };

      const response = await axios.get(`${SERVER}/banners/admin/list`, {
        params,
        withCredentials: true
      });

      setBanners(response.data.banners);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate && !editingBanner) return;
    if (!canEdit && editingBanner) return;

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'image' && formData[key] instanceof File) {
          formDataToSend.append(key, formData[key]);
        } else if (key !== 'image') {
          formDataToSend.append(key, formData[key]);
        }
      });

      let response;
      if (editingBanner) {
        response = await axios.put(`${SERVER}/banners/admin/${editingBanner.id}`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${SERVER}/banners/admin`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.status === 200 || response.status === 201) {
        fetchBanners();
        closeModal();
      }
    } catch (error) {
      console.error('Error saving banner:', error);
    }
  };

  const handleEdit = async (banner) => {
    if (!canEdit) return;
    
    setFormData({
      title: banner.title || '',
      description: banner.description || '',
      position: banner.position || '',
      image: null,
      cta_text: banner.cta_text || '',
      cta_url: banner.cta_url || '',
      text_color: banner.text_color || '#FFFFFF',
      background_color: banner.background_color || '#1D44E9',
      display_order: banner.display_order || 0,
      active: banner.active
    });
    setEditingBanner(banner);
    setShowModal(true);
  };

  const handleDelete = async (banner) => {
    if (!canDelete) return;
    
    if (window.confirm(`Are you sure you want to delete "${banner.title}"?`)) {
      try {
        await axios.delete(`${SERVER}/banners/admin/${banner.id}`, {
          withCredentials: true
        });
        fetchBanners();
      } catch (error) {
        console.error('Error deleting banner:', error);
      }
    }
  };

  const handleToggleActive = async (banner) => {
    if (!canEdit) return;
    
    try {
      await axios.put(`${SERVER}/banners/admin/${banner.id}`, {
        ...banner,
        active: !banner.active
      }, {
        withCredentials: true
      });
      fetchBanners();
    } catch (error) {
      console.error('Error toggling banner status:', error);
    }
  };

  const openCreateModal = () => {
    if (!canCreate) return;
    
    setFormData({
      title: '',
      description: '',
      position: '',
      image: null,
      cta_text: '',
      cta_url: '',
      text_color: '#FFFFFF',
      background_color: '#1D44E9',
      display_order: banners.length,
      active: true
    });
    setEditingBanner(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBanner(null);
    setFormData({
      title: '',
      description: '',
      position: '',
      image: null,
      cta_text: '',
      cta_url: '',
      text_color: '#FFFFFF',
      background_color: '#1D44E9',
      display_order: 0,
      active: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Banners</h2>
          <p className="text-slate-600">Manage promotional banners across the website</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaPlus />
            <span>Add Banner</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search banners..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Banners Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {banners.map((banner) => (
          <div key={banner.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden">
            {/* Banner Preview */}
            <div 
              className="h-32 relative overflow-hidden"
              style={{ backgroundColor: banner.background_color }}
            >
              {banner.image && (
                <img
                  src={`${SERVER}${banner.image}`}
                  alt={banner.title}
                  className="w-full h-full object-cover opacity-80"
                />
              )}
              <div className="absolute inset-0 flex items-center justify-center">
                <h3 
                  className="text-lg font-bold text-center px-4"
                  style={{ color: banner.text_color }}
                >
                  {banner.title}
                </h3>
              </div>
              
              {/* Status Badge */}
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => handleToggleActive(banner)}
                  disabled={!canEdit}
                  className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                    banner.active
                      ? 'bg-green-100/90 text-green-800 hover:bg-green-200/90'
                      : 'bg-red-100/90 text-red-800 hover:bg-red-200/90'
                  } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  {banner.active ? <FaToggleOn /> : <FaToggleOff />}
                  {banner.active ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                  {positionOptions.find(p => p.value === banner.position)?.label || banner.position}
                </span>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: banner.background_color }}
                    title="Background Color"
                  />
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: banner.text_color }}
                    title="Text Color"
                  />
                </div>
              </div>
              
              {banner.description && (
                <p className="text-slate-600 text-sm mb-4 line-clamp-2">{banner.description}</p>
              )}
              
              {banner.cta_text && (
                <div className="mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-lg">
                    <FaLink size={12} />
                    {banner.cta_text}
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                <span className="text-xs text-slate-500">Order: {banner.display_order}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(banner)}
                    disabled={!canEdit}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(banner)}
                    disabled={!canDelete}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {banners.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaImage className="text-3xl text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Banners Found</h3>
          <p className="text-slate-600 mb-6">Create your first banner to get started.</p>
          {canCreate && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
            >
              <FaPlus />
              Add Banner
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingBanner ? 'Edit Banner' : 'Add New Banner'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter banner title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Position *
                  </label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select position</option>
                    {positionOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter banner description"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Background Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    CTA Button Text
                  </label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Get Started"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    CTA Button URL
                  </label>
                  <input
                    type="url"
                    value={formData.cta_url}
                    onChange={(e) => setFormData({ ...formData, cta_url: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Background Color
                  </label>
                  <input
                    type="color"
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                    className="w-full h-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Text Color
                  </label>
                  <input
                    type="color"
                    value={formData.text_color}
                    onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                    className="w-full h-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingBanner ? 'Update Banner' : 'Create Banner'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banners;